// src/screens/admin/orders/AdminOrderView.jsx
import React, { useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiUser,
  FiPhone,
  FiMapPin,
  FiPackage,
  FiCheckCircle,
  FiCircle,
  FiClock,
  FiShoppingBag,
  FiMail,
} from "react-icons/fi";
import AdminMasterLayout from "../../../layouts/admin/AdminMasterLayout";
import AppContext from "../../../contexts/AppContext";
import "../../../assets/css/admin/screens/ordersManagement/AdminOrdersView.css";

const PLATFORM_COMMISSION_PERCENT = 7;

const AdminOrderView = () => {
  const navigate = useNavigate();
  const { baseUrlForFiles, adminSelectedOrder: orderData } =
    useContext(AppContext);

  const status = orderData?.status || "";

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!orderData) navigate("/admin/orders", { replace: true });
  }, [orderData?.id]);

  if (!orderData) return null;

  const getStatusClass = (s) => {
    const map = {
      Processing: "aov-status-processing",
      Shipped: "aov-status-shipped",
      completed: "aov-status-delivered",
      Cancelled: "aov-status-cancelled",
    };
    return map[s] || "";
  };

  const invoiceSubtotal = (orderData.cart || []).reduce(
    (sum, item) => sum + Number(item.price) * item.quantity,
    0,
  );
  const invoiceShipping = Number(orderData.total_amount) - invoiceSubtotal;

  // ── Platform commission: locked value once order is completed, estimate otherwise ──
  let commissionAmount = invoiceSubtotal * (PLATFORM_COMMISSION_PERCENT / 100);
  if (status === "completed") {
    commissionAmount = Number(orderData.commission_amount);
  }

  const timelineSteps = [
    { label: "Order Placed", done: true },
    { label: "Processing", done: status !== "Cancelled" },
    { label: "Shipped", done: status === "Shipped" || status === "completed" },
    { label: "Completed", done: status === "completed" },
  ];

  return (
    <AdminMasterLayout>
      <div className="aov-content-wrapper">
        <button
          className="aov-back-btn"
          onClick={() => navigate("/admin/orders")}
        >
          <FiArrowLeft /> Back to Orders
        </button>

        <div className="aov-header">
          <div className="aov-header-left">
            <h1 className="aov-header-id">#{orderData.id}</h1>
            <div className="aov-header-meta">
              <span className="aov-header-date">
                {new Date(orderData.created_at).toLocaleDateString("en-PK", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </span>
              <span className={`aov-header-status ${getStatusClass(status)}`}>
                {status}
              </span>
            </div>
          </div>
          <div className="aov-header-right">
            <div className="aov-header-total">
              <span className="aov-total-label">Order Total</span>
              <span className="aov-total-amount">
                Rs {Number(orderData.total_amount).toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        <div className="aov-layout">
          <div className="aov-left">
            <div className="aov-card">
              <h3 className="aov-card-title">
                <FiUser /> Customer Details
              </h3>
              <div className="aov-info-list">
                <div className="aov-info-row">
                  <div className="aov-info-icon">
                    <FiUser />
                  </div>
                  <div className="aov-info-data">
                    <span className="aov-info-label">Name</span>
                    <span className="aov-info-value">
                      {orderData.full_name}
                    </span>
                  </div>
                </div>
                <div className="aov-info-row">
                  <div className="aov-info-icon">
                    <FiPhone />
                  </div>
                  <div className="aov-info-data">
                    <span className="aov-info-label">Phone</span>
                    <span className="aov-info-value">{orderData.phone}</span>
                  </div>
                </div>
                <div className="aov-info-row">
                  <div className="aov-info-icon">
                    <FiMapPin />
                  </div>
                  <div className="aov-info-data">
                    <span className="aov-info-label">Address</span>
                    <span className="aov-info-value">{orderData.address}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="aov-card">
              <h3 className="aov-card-title">
                <FiShoppingBag /> Seller / Shop Details
              </h3>
              <div className="aov-info-list">
                <div className="aov-info-row">
                  <div className="aov-info-icon">
                    <FiShoppingBag />
                  </div>
                  <div className="aov-info-data">
                    <span className="aov-info-label">Shop Name</span>
                    <span className="aov-info-value">
                      {orderData.seller?.store_name || "—"}
                    </span>
                  </div>
                </div>
                <div className="aov-info-row">
                  <div className="aov-info-icon">
                    <FiPhone />
                  </div>
                  <div className="aov-info-data">
                    <span className="aov-info-label">Seller Phone</span>
                    <span className="aov-info-value">
                      {orderData.seller?.phone || "—"}
                    </span>
                  </div>
                </div>
                <div className="aov-info-row">
                  <div className="aov-info-icon">
                    <FiMail />
                  </div>
                  <div className="aov-info-data">
                    <span className="aov-info-label">Seller Email</span>
                    <span className="aov-info-value">
                      {orderData.seller?.email || "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="aov-right">
            <div className="aov-card">
              <h3 className="aov-card-title">
                <FiPackage /> Order Items
              </h3>
              <div className="aov-products-list">
                {(orderData.cart || []).map((item) => {
                  let imgSrc = "/placeholder.png";
                  if (item.product?.images?.length > 0) {
                    imgSrc = `${baseUrlForFiles}/${item.product.images[0].image_path}`;
                  }
                  return (
                    <div key={item.id} className="aov-product-item">
                      <div className="aov-product-main">
                        <img
                          src={imgSrc}
                          alt={item.product?.product_name}
                          style={{
                            width: 50,
                            height: 50,
                            borderRadius: 8,
                            objectFit: "cover",
                            flexShrink: 0,
                          }}
                        />
                        <div className="aov-product-info">
                          <h4 className="aov-product-name">
                            {item.product?.product_name}
                          </h4>
                          <div className="aov-product-meta">
                            <span>Qty: {item.quantity}</span>
                            <span>•</span>
                            <span className="aov-product-price">
                              Rs {Number(item.price).toLocaleString()}
                            </span>
                            {item.size && (
                              <>
                                <span>•</span>
                                <span>Size: {item.size}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="aov-products-total">
                <span className="aov-products-total-label">Total Items:</span>
                <span className="aov-products-total-count">
                  {(orderData.cart || []).reduce(
                    (sum, i) => sum + i.quantity,
                    0,
                  )}
                </span>
              </div>
            </div>

            <div className="aov-card">
              <h3 className="aov-card-title">
                <FiClock /> Order Timeline
              </h3>
              <div className="aov-timeline-list">
                {timelineSteps.map((item, index) => (
                  <div
                    key={index}
                    className={`aov-timeline-item ${item.done ? "completed" : "pending"}`}
                  >
                    <div className="aov-timeline-icon">
                      {item.done ? <FiCheckCircle /> : <FiCircle />}
                    </div>
                    <div className="aov-timeline-content">
                      <span className="aov-timeline-status">{item.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="aov-card aov-payment-fullwidth">
          <h3 className="aov-card-title">
            <span className="aov-rs-badge">Rs</span> Payment Breakdown
          </h3>
          <div className="aov-payment-breakdown">
            <div className="aov-payment-row">
              <span>Product Price</span>
              <span>Rs {invoiceSubtotal.toLocaleString()}</span>
            </div>
            <div className="aov-payment-row">
              <span>Delivery Charges</span>
              <span>Rs {invoiceShipping.toLocaleString()}</span>
            </div>
            <div className="aov-payment-row aov-payment-row-total">
              <span>Order Total</span>
              <span>Rs {Number(orderData.total_amount).toLocaleString()}</span>
            </div>
            <div className="aov-payment-row aov-payment-row-commission">
              <span>Platform Commission ({PLATFORM_COMMISSION_PERCENT}%)</span>
              <span>Rs {commissionAmount.toLocaleString()}</span>
            </div>
            {status === "completed" ? (
              <p className="aov-payment-note">
                This commission has been deducted from the seller's wallet
                balance for this order.
              </p>
            ) : (
              <p className="aov-payment-note">
                This commission will be deducted from the seller's wallet
                balance once the order is marked Completed.
              </p>
            )}
          </div>
        </div>
      </div>
    </AdminMasterLayout>
  );
};

export default AdminOrderView;